import React, { useEffect, useRef, useState } from 'react';
import './LivingOrb.css';

/**
 * LivingOrb — the unified morph element for Vella 2.0 (Operation Metamorph).
 *
 * ONE DOM element, THREE placements, FIVE states. It is BOTH the Living Orb
 * AND the generating card — the orb morphs INTO the card and back, never
 * unmounting mid-morph.
 *
 * Visual states (drive the internal "brain" — liquid currents, neural flashes,
 * twinkling stars, rising motes, flickering core):
 *   idle | listening | thinking | success
 *
 * Modes (root class `.as-orb` ⇄ `.as-card` — the SHELL is the morph surface):
 *   orb  — glass sphere
 *   card — 560×250 rounded generating card with a shimmering verb + track
 *
 * Placements (wrapper position, glides once per conversation):
 *   centerstage — full 300px, centered mid-page (empty state)
 *   docked      — scaled to ~120px, locked above the chat bar
 *
 * The internal keyframes (b1/b2/b3/core/flash/star/mote/breathe) are VERBATIM
 * from the approved spec — see LivingOrb.css. Only shell geometry and wrapper
 * position change between states.
 */

// Verb sets cycled while the card is generating, keyed by task type.
const GEN_WORDS = {
  website:  ['Designing', 'Composing', 'Refining', 'Polishing'],
  image:    ['Sketching', 'Lighting', 'Rendering', 'Polishing'],
  campaign: ['Researching', 'Planning', 'Budgeting', 'Polishing'],
  ads:      ['Drafting', 'Targeting', 'Optimizing', 'Launching'],
  video:    ['Storyboarding', 'Filming', 'Cutting', 'Polishing'],
};

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function LivingOrb({
  state = 'idle',            // idle | listening | thinking | success
  mode = 'orb',              // orb | card
  placement = 'centerstage', // centerstage | docked
  taskType = 'website',      // website | image | campaign | ads | video
  dockOffset,                // px above the viewport bottom when docked
  onStop,                    // fired by the card's Stop button
  onClick,                   // fired when the (center-stage) orb is clicked
  style,
}) {
  const outerRef = useRef(null);
  const prevState = useRef(state);

  // Success burst ring: fire exactly ONCE per entry into success.
  useEffect(() => {
    if (state === 'success' && prevState.current !== 'success' && outerRef.current) {
      const ring = document.createElement('div');
      ring.className = 'burst';
      outerRef.current.appendChild(ring);
      setTimeout(() => { if (ring.parentNode) ring.remove(); }, 1500);
    }
    prevState.current = state;
  }, [state]);

  // Verb cycling — runs ONLY while in card mode; resets to the first verb on
  // morph-back. prefers-reduced-motion pins to the first verb (no cycling).
  const words = GEN_WORDS[taskType] || GEN_WORDS.website;
  const [verbIdx, setVerbIdx] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (mode !== 'card') { setVerbIdx(0); setFading(false); return; }
    if (prefersReducedMotion()) { setVerbIdx(0); return; }
    let idx = 0;
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        idx = (idx + 1) % words.length;
        setVerbIdx(idx);
        setFading(false);
      }, 260);
    }, 2400);
    return () => clearInterval(id);
    // words is derived from taskType; re-arm on mode/taskType change.
  }, [mode, taskType]);
  const verb = words[verbIdx] || words[0];

  const rootStyle = dockOffset != null
    ? { ...style, '--vella-dock-offset': `${dockOffset}px` }
    : style;

  return (
    <div className={`vella-orb as-${mode} orb-${placement} state-${state}`} style={rootStyle} onClick={onClick}>
      <div className="orb-outer" ref={outerRef}>
        <div className="attn" />
        <div className="shell">
          <div className="b1" />
          <div className="b3" />
          <div className="b2" />
          <div className="flash f1" />
          <div className="flash f2" />
          <div className="flash f3" />
          <div className="core" />
          <span className="star" style={{ top: '30%', left: '36%', width: '2.5px', height: '2.5px', animationDelay: '0s' }} />
          <span className="star" style={{ top: '46%', left: '64%', width: '2px', height: '2px', animationDelay: '1.2s' }} />
          <span className="star" style={{ top: '64%', left: '44%', width: '2px', height: '2px', animationDelay: '2.4s' }} />
          <span className="star" style={{ top: '36%', left: '56%', width: '1.5px', height: '1.5px', animationDelay: '3.1s' }} />
          <span className="star" style={{ top: '56%', left: '28%', width: '2px', height: '2px', animationDelay: '1.8s' }} />
          <span className="star" style={{ top: '70%', left: '62%', width: '1.5px', height: '1.5px', animationDelay: '0.6s' }} />
          <span className="mote" style={{ left: '36%', animationDelay: '0s' }} />
          <span className="mote" style={{ left: '50%', animationDelay: '2.4s' }} />
          <span className="mote" style={{ left: '63%', animationDelay: '4.2s' }} />
          <span className="mote" style={{ left: '44%', animationDelay: '6s' }} />
          <div className="hl" />
          {/* Card-only content — always mounted, opacity 0 in orb mode, fades in
              as the shell morphs to a card. NEVER unmounted mid-morph. */}
          <div className="gen-content">
            <div className={`gen-word${fading ? ' fading' : ''}`} id="genWord">{verb}</div>
            <div className="gen-track" />
          </div>
        </div>
      </div>
      {/* Stop button — outside the shell, inside the wrapper. Hidden in orb
          mode, revealed in card mode. */}
      <button
        type="button"
        className="orb-stop"
        data-no-vella-ring="true"
        onClick={() => onStop?.()}
        aria-label="Stop generating"
        tabIndex={mode === 'card' ? 0 : -1}
      >
        <i /> Stop
      </button>
    </div>
  );
}

export default LivingOrb;
